// docs/147 PG3: Phaser draws every live particle of an emitter, on screen or not: a transform, four
// corners and a quad written to the batch each, every frame. The weather and the air spawn over the
// whole board (a rain on an exploration for twenty keeps ~5,000 streaks alive, nearly all of them
// off screen), so most of that work and most of the bytes sent to the GPU drew nothing. Here an
// emitter is handed only the particles whose quad can reach the camera's view; the rest would have
// covered no pixel, so the picture is the same. Every particle still lives, moves and dies as before.
import Phaser from 'phaser';

type Emitter = Phaser.GameObjects.Particles.ParticleEmitter & { alive: Phaser.GameObjects.Particles.Particle[]; sortCallback?: unknown };
type Renderer = (renderer: unknown, emitter: Emitter, ctx: { camera: Phaser.Cameras.Scene2D.Camera }, parent?: Phaser.GameObjects.Components.TransformMatrix) => void;

/** Emitters with fewer particles are drawn as they are (the check would cost more than it saves). */
const MIN_PARTICLES = 24;
/** Screen px of slack round the view (a quad's edge rounding, a pixel-snapped camera). */
const SLACK_PX = 4;

let installed = false;
/** Particles handed over vs skipped (tests and the bench). */
export const cullStats = { drawn: 0, skipped: 0 };

const identity = (m?: Phaser.GameObjects.Components.TransformMatrix): boolean => {
  if (!m) return true;
  const a = m.matrix;
  return a[0] === 1 && a[1] === 0 && a[2] === 0 && a[3] === 1 && a[4] === 0 && a[5] === 0;
};

/** Install the cull on every particle emitter (once; WebGL only). */
export function installParticleCull(): void {
  if (installed) return;
  installed = true;
  const proto = Phaser.GameObjects.Particles.ParticleEmitter.prototype as unknown as { renderWebGL: Renderer };
  const draw = proto.renderWebGL;
  if (!draw) return;
  const keep: Phaser.GameObjects.Particles.Particle[] = [];

  proto.renderWebGL = function (renderer, emitter, ctx, parent) {
    const alive = emitter.alive;
    const cam = ctx.camera;
    // only the plain case: particles in board px under an unrotated, unshaken camera; anything
    // else (a sorted emitter, one moved, scaled or scrolled apart, inside a moved container) draws as before
    if (alive.length < MIN_PARTICLES || emitter.sortCallback || emitter.x !== 0 || emitter.y !== 0 || emitter.rotation !== 0 || emitter.scaleX !== 1 || emitter.scaleY !== 1
      || emitter.scrollFactorX !== 1 || emitter.scrollFactorY !== 1 || !identity(parent) || (cam as unknown as { rotation: number }).rotation !== 0 || cam.shakeEffect.isRunning) {
      draw.call(this, renderer, emitter, ctx, parent);
      return;
    }
    // what the camera shows, in board px (Camera.preRender: screen = origin + zoom·(world − scroll − origin))
    const ox = cam.width * cam.originX, oy = cam.height * cam.originY;
    const slackX = SLACK_PX / cam.zoomX, slackY = SLACK_PX / cam.zoomY;
    const x0 = cam.scrollX + ox - ox / cam.zoomX - slackX, y0 = cam.scrollY + oy - oy / cam.zoomY - slackY;
    const x1 = x0 + cam.width / cam.zoomX + 2 * slackX, y1 = y0 + cam.height / cam.zoomY + 2 * slackY;
    keep.length = 0;
    for (let i = 0; i < alive.length; i++) {
      const p = alive[i]!;
      // the quad is centred on the particle and turns about it: it stays within this circle
      const f = p.frame, r = 0.5 * Math.hypot(f.width * p.scaleX, f.height * p.scaleY);
      if (p.x + r < x0 || p.x - r > x1 || p.y + r < y0 || p.y - r > y1) continue;
      keep.push(p);
    }
    cullStats.drawn += keep.length; cullStats.skipped += alive.length - keep.length;
    emitter.alive = keep;
    try { draw.call(this, renderer, emitter, ctx, parent); } finally { emitter.alive = alive; }
  };
}
